import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react';

import {
  CURVE_PRESETS,
  addCurveKey,
  clamp01,
  curvesRange,
  moveCurveKey,
  presetKeys,
  r3,
  removeCurveKey,
  reverseCurve,
  sampleCurve,
  evalCurve,
  type CurveKeys,
} from '../../lib/curves.js';
import { IconButton, MenuButton, NumberField, type MenuItem } from './controls.js';
import { Icon } from './icons.js';
import './curves.css';

/**
 * Reusable keyframe curve editor (Unity's curve editor in miniature).
 *
 * Edits one curve, or two (a min / max pair drawn as a band, Unity's "Random
 * between two curves"). Drag a key to move it (Ctrl snaps to a 0.05 grid),
 * double-click empty space to add a key on the active curve, right-click or
 * Delete removes the selected key; the selected key's time and value can be
 * typed. Linear / smooth interpolation (when `onSmoothChange` is given),
 * presets and reverse act on the active curve. Keys are `[[time, value]]`
 * with time 0..1 (lib/curves.ts); the value axis fits the keys unless
 * `range` fixes it. `onGestureStart` / `onGestureEnd` bracket each drag so
 * callers can merge it into one undo step.
 */
export interface CurveEditorProps {
  /** One curve, or two (min, max). */
  curves: readonly CurveKeys[];
  onChange: (index: number, keys: CurveKeys) => void;
  smooth?: boolean;
  /** Shows the Linear / Smooth switch. */
  onSmoothChange?: (smooth: boolean) => void;
  /** Fixed value axis; default: fits the keys, at least 0..1. */
  range?: [number, number];
  /** Step of the key value field. */
  step?: number;
  /** Graph height in CSS pixels. */
  height?: number;
  /** Names of the two curves. */
  labels?: [string, string];
  /** Normalized time drawn as a playhead (e.g. the preview's current time). */
  playhead?: number | null;
  /** What the time axis means, shown under the graph ("lifetime", "cycle"). */
  timeLabel?: string;
  onGestureStart?: () => void;
  onGestureEnd?: () => void;
}

interface Drag {
  curve: number;
  index: number;
  keys: CurveKeys;
  range: [number, number];
  pointer: number;
}

const PAD_X = 30;
const PAD_Y = 10;
const CURVE_CLASSES = ['is-min', 'is-max'];

export function CurveEditor({
  curves,
  onChange,
  smooth = false,
  onSmoothChange,
  range,
  step = 0.05,
  height = 120,
  labels = ['Min', 'Max'],
  playhead = null,
  timeLabel,
  onGestureStart,
  onGestureEnd,
}: CurveEditorProps) {
  const host = useRef<HTMLDivElement>(null);
  const svg = useRef<SVGSVGElement>(null);
  const drag = useRef<Drag | null>(null);
  const [width, setWidth] = useState(260);
  const [sel, setSel] = useState<{ curve: number; key: number } | null>(null);
  const [active, setActive] = useState(curves.length - 1);
  const [, redraw] = useState(0);

  useEffect(() => {
    const el = host.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 40) setWidth(Math.round(w));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const W = width;
  const H = height;
  let [lo, hi] = range ?? curvesRange(curves);
  if (drag.current) [lo, hi] = drag.current.range;
  const span = hi - lo || 1;
  const toX = (t: number) => PAD_X + clamp01(t) * (W - PAD_X - 8);
  const toY = (v: number) => PAD_Y + (1 - (v - lo) / span) * (H - 2 * PAD_Y);
  const fromPointer = (e: { clientX: number; clientY: number; ctrlKey?: boolean }): [number, number] => {
    const rect = svg.current!.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * W;
    const y = ((e.clientY - rect.top) / rect.height) * H;
    let t = clamp01((x - PAD_X) / (W - PAD_X - 8));
    let v = lo + (1 - (y - PAD_Y) / (H - 2 * PAD_Y)) * span;
    if (range) v = Math.min(range[1], Math.max(range[0], v));
    if (e.ctrlKey) {
      t = Math.round(t * 20) / 20;
      v = Math.round(v / (span / 20)) * (span / 20);
    }
    return [r3(t), r3(v)];
  };

  const keysOf = (c: number): CurveKeys => (curves[c] ?? []) as CurveKeys;
  const activeCurve = Math.max(0, Math.min(active, curves.length - 1));
  const selKey = sel ? curves[sel.curve]?.[sel.key] : undefined;
  const selected = selKey ? sel : null;

  function setKey(curve: number, index: number, t: number, v: number, base = keysOf(curve)) {
    const res = moveCurveKey(base, index, t, v);
    onChange(curve, res.keys);
    setSel({ curve, key: res.index });
    return res;
  }

  function removeSelected() {
    if (!selected || keysOf(selected.curve).length <= 1) return;
    onChange(selected.curve, removeCurveKey(keysOf(selected.curve), selected.key));
    setSel(null);
  }

  function startDrag(e: ReactPointerEvent<SVGCircleElement>, curve: number, index: number) {
    e.stopPropagation();
    e.preventDefault();
    if (e.button === 2) return;
    svg.current?.focus();
    svg.current?.setPointerCapture(e.pointerId);
    setSel({ curve, key: index });
    setActive(curve);
    drag.current = { curve, index, keys: keysOf(curve), range: [lo, hi], pointer: e.pointerId };
    onGestureStart?.();
  }

  function onPointerMove(e: ReactPointerEvent<SVGSVGElement>) {
    const d = drag.current;
    if (!d || d.pointer !== e.pointerId) return;
    const [t, v] = fromPointer(e);
    const res = setKey(d.curve, d.index, t, v, d.keys);
    d.keys = res.keys;
    d.index = res.index;
  }

  function endDrag(e: ReactPointerEvent<SVGSVGElement>) {
    if (drag.current?.pointer !== e.pointerId) return;
    drag.current = null;
    redraw((n) => n + 1);
    onGestureEnd?.();
  }

  function onDoubleClick(e: ReactMouseEvent<SVGSVGElement>) {
    const [t] = fromPointer(e);
    const [, v] = fromPointer(e);
    const onCurve = evalCurve(keysOf(activeCurve), t, smooth);
    // Close to the curve: add on it; elsewhere: where clicked.
    const near = Math.abs(toY(onCurve) - toY(v)) < 8;
    const res = addCurveKey(keysOf(activeCurve), t, near ? onCurve : v, smooth);
    onChange(activeCurve, res.keys);
    setSel({ curve: activeCurve, key: res.index });
  }

  const presetItems: MenuItem[] = CURVE_PRESETS.map((p) => ({
    id: p.name,
    label: p.name,
    run: () => {
      // Presets keep the curve's current value span (0..1 when flat).
      const [plo, phi] = curvesRange([keysOf(activeCurve)], [Infinity, -Infinity]);
      const flat = !(phi > plo);
      const a = flat ? 0 : plo;
      const b = flat ? (Math.abs(phi) > 1e-6 && Number.isFinite(phi) ? phi : 1) : phi;
      onChange(activeCurve, presetKeys(p, a, b));
      if (onSmoothChange && p.smooth !== smooth) onSmoothChange(p.smooth);
      setSel(null);
    },
  }));

  const line = (keys: CurveKeys) =>
    sampleCurve(keys, smooth)
      .map(([t, v]) => `${r3(toX(t))},${r3(toY(v))}`)
      .join(' ');

  let band: string | null = null;
  if (curves.length === 2) {
    const a = sampleCurve(keysOf(0), true, 48).map(([t]) => t);
    const times = smooth ? a : [...new Set([0, 1, ...curves.flatMap((c) => c.map((k) => clamp01(k[0])))])].sort((x, y) => x - y);
    const top = times.map((t) => `${r3(toX(t))},${r3(toY(evalCurve(keysOf(0), t, smooth)))}`);
    const bottom = [...times].reverse().map((t) => `${r3(toX(t))},${r3(toY(evalCurve(keysOf(1), t, smooth)))}`);
    band = [...top, ...bottom].join(' ');
  }
  const zeroY = lo < 0 && hi > 0 ? toY(0) : null;
  const midV = (lo + hi) / 2;

  return (
    <div className="cv-editor" ref={host}>
      <svg
        ref={svg}
        className="cv-graph"
        width="100%"
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        tabIndex={0}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDoubleClick={onDoubleClick}
        onPointerDown={(e) => {
          if (e.target === e.currentTarget || (e.target as Element).classList.contains('cv-bg')) setSel(null);
        }}
        onContextMenu={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          if (e.key === 'Delete' || e.key === 'Backspace') {
            e.preventDefault();
            e.stopPropagation();
            removeSelected();
          }
        }}
        role="application"
        aria-label="Curve editor: drag keys, double-click to add, Delete or right-click to remove"
      >
        <rect className="cv-bg" x={PAD_X} y={PAD_Y} width={W - PAD_X - 8} height={H - 2 * PAD_Y} />
        {[0.25, 0.5, 0.75].map((t) => (
          <line key={t} className="cv-grid" x1={toX(t)} x2={toX(t)} y1={PAD_Y} y2={H - PAD_Y} />
        ))}
        <line className="cv-grid" x1={PAD_X} x2={W - 8} y1={toY(midV)} y2={toY(midV)} />
        {zeroY !== null && <line className="cv-zero" x1={PAD_X} x2={W - 8} y1={zeroY} y2={zeroY} />}
        <text className="cv-label" x={PAD_X - 4} y={PAD_Y + 4} textAnchor="end">
          {r3(hi)}
        </text>
        <text className="cv-label" x={PAD_X - 4} y={toY(midV) + 3} textAnchor="end">
          {r3(midV)}
        </text>
        <text className="cv-label" x={PAD_X - 4} y={H - PAD_Y + 3} textAnchor="end">
          {r3(lo)}
        </text>
        {band && <polygon className="cv-band" points={band} />}
        {playhead !== null && playhead >= 0 && playhead <= 1 && (
          <line className="cv-playhead" x1={toX(playhead)} x2={toX(playhead)} y1={PAD_Y} y2={H - PAD_Y} />
        )}
        {curves.map((keys, c) => (
          <polyline
            key={c}
            className={`cv-line ${curves.length === 2 ? CURVE_CLASSES[c] : ''}${c === activeCurve ? ' is-active' : ''}`}
            points={line(keys as CurveKeys)}
          />
        ))}
        {curves.map((keys, c) =>
          keys.map((k, i) => {
            const isSel = selected?.curve === c && selected.key === i;
            return (
              <circle
                key={`${c}-${i}`}
                className={`cv-key ${curves.length === 2 ? CURVE_CLASSES[c] : ''}${isSel ? ' is-selected' : ''}`}
                cx={toX(k[0])}
                cy={toY(k[1])}
                r={isSel ? 5 : 4}
                onPointerDown={(e) => startDrag(e, c, i)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (keys.length > 1) {
                    onChange(c, removeCurveKey(keys as CurveKeys, i));
                    setSel(null);
                  }
                }}
              >
                <title>{`${curves.length === 2 ? `${labels[c]}: ` : ''}t ${r3(k[0])}, ${r3(k[1])} (right-click to remove)`}</title>
              </circle>
            );
          }),
        )}
      </svg>
      <div className="cv-row">
        {selected && selKey ? (
          <>
            <NumberField
              label="T"
              title="Key time (0..1)"
              value={selKey[0]}
              step={0.01}
              min={0}
              max={1}
              onChange={(t) => setKey(selected.curve, selected.key, t, selKey[1])}
            />
            <NumberField
              label="V"
              title="Key value"
              value={selKey[1]}
              step={step}
              min={range?.[0]}
              max={range?.[1]}
              onChange={(v) => setKey(selected.curve, selected.key, selKey[0], v)}
            />
            <IconButton icon="trash" title="Delete key" size={13} disabled={keysOf(selected.curve).length <= 1} onClick={removeSelected} />
          </>
        ) : (
          <span className="cv-hint">
            {curves.length === 2 ? `${labels[activeCurve]} curve · ` : ''}
            {timeLabel ? `0..1 of the ${timeLabel} · ` : ''}double-click adds a key
          </span>
        )}
        {curves.length === 2 && (
          <div className="cv-seg" role="group" aria-label="Active curve">
            {[0, 1].map((c) => (
              <button key={c} type="button" className={c === activeCurve ? 'is-on' : ''} onClick={() => setActive(c)}>
                {labels[c]}
              </button>
            ))}
          </div>
        )}
        {onSmoothChange && (
          <div className="cv-seg" role="group" aria-label="Interpolation">
            <button type="button" className={!smooth ? 'is-on' : ''} title="Straight lines between keys" onClick={() => onSmoothChange(false)}>
              Linear
            </button>
            <button type="button" className={smooth ? 'is-on' : ''} title="Smooth (eased) between keys" onClick={() => onSmoothChange(true)}>
              Smooth
            </button>
          </div>
        )}
        <IconButton
          icon="mirror"
          title="Reverse in time"
          size={13}
          onClick={() => {
            onChange(activeCurve, reverseCurve(keysOf(activeCurve)));
            setSel(null);
          }}
        />
        <MenuButton items={presetItems} className="ibtn cv-presets" title="Curve presets">
          <Icon name="list" size={13} />
        </MenuButton>
      </div>
    </div>
  );
}
